import { useMemo, useState, useContext } from "react";
import {
  Box,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
  ScrollArea,
  Popover,
  UnstyledButton,
  ThemeIcon,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import {
  IconChevronDown,
  IconSearch,
  IconCheck,
  IconAlertCircle,
} from "@tabler/icons-react";
import { AuthContext } from "../contexts/AuthContext";
import useListKeyboardNav from "../hooks/useListKeyboardNav";
import OrgLogo from "./admin/OrgLogo";
import classes from "../styles/OrgPicker.module.css";

// The "Working In" switch: when the user is Working As a Contractor, this lets
// them choose which organisation's data they are working in - either the
// contractor's own organisation, or one of its linked Asset Owners. The choice
// becomes the dataScopeOrgId used downstream. "Own org" is represented by
// targetOrgId === contractorId. The linked asset owner options are resolved
// app-wide by AuthContext from /organisations/<id>/asset-owners (seeded from
// /auth/me for the primary org), so this
// component just renders and filters them. Reconciling an invalid persisted
// target also lives in AuthContext so it happens whether or not this is mounted.
const TargetOrgBadge = ({ contractor }) => {
  const contractorId = contractor?.id;
  const {
    targetOrgId,
    setTargetOrgId,
    linkedAssetOwners,
    linkedAssetOwnersLoading: isLoading,
    linkedAssetOwnersError: isError,
  } = useContext(AuthContext);
  const [opened, setOpened] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(query.trim(), 250);

  // Client-side substring filter over the contractor's linked asset owners. The
  // list is small (a single contractor's links), so filtering here avoids a
  // request per keystroke. Matching the unfiltered list resolves the current
  // selection even mid-search.
  const assetOwners = useMemo(() => {
    if (!debouncedQuery) return linkedAssetOwners;
    const q = debouncedQuery.toLowerCase();
    return linkedAssetOwners.filter((o) =>
      [o.fullName, o.shortName]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q))
    );
  }, [linkedAssetOwners, debouncedQuery]);

  const isOwn = String(targetOrgId) === String(contractorId);
  const activeAssetOwner = useMemo(
    () =>
      linkedAssetOwners.find((o) => String(o.id) === String(targetOrgId)) ||
      null,
    [linkedAssetOwners, targetOrgId]
  );
  // Always resolves to a real org - falls back to the contractor's own org, so
  // the badge never shows an empty "select" placeholder.
  const displayOrg = isOwn ? contractor : activeAssetOwner || contractor;

  const handlePick = (id) => {
    setTargetOrgId(String(id));
    setOpened(false);
    setQuery("");
  };

  // Pin the contractor's own org at the top of the list (outside search) so it
  // is always reachable; the searchable asset owners follow.
  const showOwnRow = !debouncedQuery && !!contractor;

  // The own-org row plus the asset owners, in render order, so the keyboard
  // highlight treats them as one list.
  const flatOptions = useMemo(
    () => [...(showOwnRow ? [contractorId] : []), ...assetOwners.map((o) => o.id)],
    [showOwnRow, contractorId, assetOwners]
  );

  const { activeIndex, handleKeyDown, getItemProps, inputRef } =
    useListKeyboardNav({
      opened,
      itemCount: flatOptions.length,
      initialIndex: Math.max(
        flatOptions.findIndex((id) => String(id) === String(targetOrgId)),
        0
      ),
      onSelect: (i) => handlePick(flatOptions[i]),
      onClose: () => setOpened(false),
    });

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      offset={6}
      shadow="md"
      width={320}
      withinPortal
    >
      <Popover.Target>
        <UnstyledButton
          onClick={() => setOpened((v) => !v)}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          data-testid="target-org-badge"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            height: 42,
            padding: "0 12px 0 6px",
            maxWidth: 280,
            backgroundColor:
              opened || hovered
                ? "var(--mantine-color-default-hover)"
                : "transparent",
            border: "none",
            borderRadius: 8,
            color: "var(--mantine-color-text)",
            transition: "background-color 100ms",
          }}
        >
          <OrgLogo org={displayOrg} size={28} />
          <Box style={{ minWidth: 0, flex: 1 }}>
            <Text
              size="9px"
              fw={500}
              c="dimmed"
              tt="uppercase"
              style={{ letterSpacing: "0.06em", lineHeight: 1.1 }}
            >
              Working In
            </Text>
            <Text
              size="xs"
              fw={500}
              truncate
              mt={2}
              style={{ lineHeight: 1.1, minWidth: 0 }}
            >
              {displayOrg?.fullName || displayOrg?.shortName}
            </Text>
          </Box>
          <IconChevronDown size={12} style={{ flexShrink: 0 }} />
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown p={6}>
        <Box px={4} pt={2} pb={6}>
          <TextInput
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            onKeyDown={handleKeyDown}
            placeholder="Switch organisation"
            size="xs"
            leftSection={<IconSearch size={12} />}
            rightSection={isLoading ? <Loader size={12} color="gray" /> : null}
            role="combobox"
            aria-expanded={opened}
            aria-controls="target-org-listbox"
            aria-activedescendant={
              flatOptions[activeIndex] !== undefined
                ? `target-org-option-${flatOptions[activeIndex]}`
                : undefined
            }
          />
        </Box>
        <ScrollArea.Autosize mah={360} type="auto">
          <Stack gap={2} id="target-org-listbox" role="listbox">
            {showOwnRow && (
              <UnstyledButton
                id={`target-org-option-${contractorId}`}
                role="option"
                aria-selected={isOwn}
                onClick={() => handlePick(contractorId)}
                data-testid="target-org-option-own"
                data-selected={isOwn || undefined}
                className={classes.option}
                {...getItemProps(0)}
              >
                <OrgLogo org={contractor} size={26} />
                <Box style={{ minWidth: 0 }}>
                  <Text size="xs" fw={500} truncate>
                    {contractor.fullName || contractor.shortName}
                  </Text>
                  <Text size="10px" c="dimmed" mt={2}>
                    Your organisation
                  </Text>
                </Box>
                {isOwn && (
                  <ThemeIcon size="xs" variant="transparent" color="teal">
                    <IconCheck size={12} />
                  </ThemeIcon>
                )}
              </UnstyledButton>
            )}

            {isError && (
              <Group gap={6} justify="center" py="md" c="dimmed">
                <IconAlertCircle size={14} />
                <Text size="xs">Could not load asset owners.</Text>
              </Group>
            )}
            {!isError && !isLoading && assetOwners.length === 0 && (
              <Text size="xs" c="dimmed" ta="center" py="sm">
                {debouncedQuery
                  ? `No matches for "${debouncedQuery}"`
                  : "No linked asset owners"}
              </Text>
            )}
            {assetOwners.map((o, i) => {
              const sel = !isOwn && String(o.id) === String(targetOrgId);
              return (
                <UnstyledButton
                  key={o.id}
                  id={`target-org-option-${o.id}`}
                  role="option"
                  aria-selected={sel}
                  onClick={() => handlePick(o.id)}
                  data-testid={`target-org-option-${o.id}`}
                  data-selected={sel || undefined}
                  className={classes.option}
                  {...getItemProps(showOwnRow ? i + 1 : i)}
                >
                  <OrgLogo org={o} size={26} />
                  <Box style={{ minWidth: 0 }}>
                    <Text size="xs" fw={500} truncate>
                      {o.fullName}
                    </Text>
                    {o.orgType && (
                      <Text size="10px" c="dimmed" mt={2}>
                        {String(o.orgType).replace(/_/g, " ")}
                      </Text>
                    )}
                  </Box>
                  {sel && (
                    <ThemeIcon size="xs" variant="transparent" color="teal">
                      <IconCheck size={12} />
                    </ThemeIcon>
                  )}
                </UnstyledButton>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
};

export default TargetOrgBadge;
